import React, { useEffect, useRef } from 'react';
import { Button } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';

import { useOptionConfigurator } from './OptionConfiguratorContext';
import { useMobileActions } from './MobileActionsContext';
import { useIsCompactChrome, useCompleteInTopBar } from './useConfiguratorLayout';
import { useConfiguratorPrimaryActions, PrimaryCompleteControl } from './configuratorPrimaryActions';
import theme from './MobileConfiguratorActions.module.scss';

interface MobileConfiguratorActionsProps {
    mode: 'showroom' | 'immersive';
    /** Immersive only: where the Back (close) button navigates. */
    backTo?: string;
}

/**
 * Bottom action bar for the compact (immersive + showroom) configurator — shown with the compact
 * chrome (<960px). It carries:
 *  - while an option-group detail is open: the Confirm selection / Close controls;
 *  - otherwise, below 768px: the complete action (sign-and-send / host complete).
 * The close (back) action lives in the top bar, and at/above 768px the complete does too. Wider
 * viewports (>=960px) use the desktop bar and render the option-detail footer in-panel, so this bar
 * doesn't show there at all.
 *
 * Sits as the last flex child of the shell's `.fit-height` column, so it's pinned below the scrolling
 * content without overlaying it.
 */
export const MobileConfiguratorActions: React.FC<MobileConfiguratorActionsProps> = ({ mode, backTo }) => {
    const isCompactChrome = useIsCompactChrome();
    const completeInTopBar = useCompleteInTopBar();
    const { projectData, tabsEnabled } = useOptionConfigurator();
    const detail = useMobileActions()?.detail ?? null;
    const { complete } = useConfiguratorPrimaryActions(mode, backTo);

    // When the bar swaps between the in-detail Confirm/Close controls and the room-level complete
    // action, a button is replaced at the SAME screen position. On touch (notably the showroom iframe)
    // a tap can emit a follow-up synthesized click that lands on the replacement — e.g. confirming a
    // selection "falls through" to Sign & Send. Arm a brief window on every swap and swallow taps on
    // the bar during it (capture phase, so it runs before the buttons' handlers).
    const hasDetail = !!detail;
    const suppressUntilRef = useRef(0);
    const prevHasDetailRef = useRef(hasDetail);
    useEffect(() => {
        if (prevHasDetailRef.current !== hasDetail) {
            prevHasDetailRef.current = hasDetail;
            suppressUntilRef.current = Date.now() + 400;
        }
    }, [hasDetail]);
    const swallowGhostTap = (e: React.MouseEvent) => {
        if (Date.now() < suppressUntilRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    // Only when the compact chrome is active (<960px). Wider viewports use the desktop bar (top) for
    // the back/complete actions and render the option-detail footer in-panel (see GroupOptionDetail).
    // Hidden together with the top chrome (tabsEnabled) so sub-flows that hide the tabs hide these too.
    if (!isCompactChrome || !tabsEnabled || !projectData) {
        return null;
    }

    // While an option-group detail is open, the bar shows its Confirm selection / Close controls
    // (registered by GroupOptionDetail) — on both phones and tablets.
    if (detail) {
        return (
            <div className={theme.actions} onClickCapture={swallowGhostTap}>
                {/* Show exactly one action: Confirm once something is picked (the choice is already
                    persisted on click), otherwise Close as the escape. */}
                <div className={theme.item}>
                    {detail.hasSelection ? (
                        <Button variant="primary" onClick={detail.onClose}>
                            <Trans>Close</Trans>
                        </Button>
                    ) : (
                        <Button variant="tertiary" onClick={detail.onClose}>
                            <Trans>Close</Trans>
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // Room-level actions: the close (back) lives in the top bar, and at/above 768px the complete does
    // too — so the bottom bar only carries the complete action below 768px (and only if one exists).
    if (completeInTopBar || !complete) {
        return null;
    }

    return (
        <div className={theme.actions} onClickCapture={swallowGhostTap}>
            <div className={theme.item}>
                <PrimaryCompleteControl complete={complete} />
            </div>
        </div>
    );
};
